import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "closeable", "onCloseClick", "processing", "use", "size", "fileName", "fileInfo", "fileType", "renderPreview"];
import { CloseButton } from '../CloseButton/CloseButton';
import { useIconSize } from 'foundations-assets/icons/useIconSize';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import styled, { useTheme } from 'foundations-theming/utils';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import devLogger from 'react-utils/devLogger';
import { StyledText } from '../../../components/Text/Text';
import { FileAttachmentProvider } from './FileAttachmentProvider';
import { Media } from '../Media';
import { StyledFlex } from '../../../components/Flex/Flex';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { PdfFileIcon } from 'foundations-assets/icons/components/PdfFileIcon';
import { TruncateString } from '../TruncateString';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultFilePreview = ({
  fileType,
  size
}) => {
  const calculatedSize = size === 'sm' ? '300' : '800';
  const Icon = fileType === 'application/pdf' ? PdfFileIcon : FileIcon;
  return /*#__PURE__*/_jsx(Icon, {
    size: calculatedSize
  });
};
const StyledFlexWrapper = styled.div.withConfig({
  displayName: "FileAttachment__StyledFlexWrapper"
})(({
  $cssStyles,
  $size,
  $use,
  $closeable,
  theme
}) => {
  return Object.assign({
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'space-between',
    borderRadius: theme.borderRadius['100'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`,
    maxWidth: '100%',
    backgroundColor: $use === 'on-dark' ? theme.color['fill-surface-default'] : theme.color['fill-field-default'],
    display: $size === 'sm' ? 'flex' : 'inline-flex',
    padding: $size === 'sm' ? `${theme.space['200']} ${theme.space['300']}` : `${theme.space['300']}`
  }, $closeable && $size !== 'sm' && {
    paddingInlineEnd: `${theme.space['800']}`
  }, $size === 'sm' && {
    inlineSize: '100%'
  }, {
    [`& ${StyledText}`]: {
      lineHeight: '16px'
    },
    [`& ${StyledFlex}:first-of-type`]: {
      inlineSize: 'auto',
      columnGap: $size === 'sm' ? theme.space['200'] : undefined,
      position: 'relative',
      minInlineSize: '0',
      display: 'flex',
      alignItems: 'center',
      gap: $size === 'sm' ? theme.space['200'] : undefined,
      '> *': {
        minWidth: 0
      }
    }
  }, $cssStyles);
});
const StyledAttachmentPreview = styled.div.withConfig({
  displayName: "FileAttachment__StyledAttachmentPreview"
})(({
  $processing,
  $previewSize,
  theme
}) => {
  return {
    order: 1,
    display: 'flex',
    alignItems: 'center',
    flexShrink: '0',
    flexBasis: 'auto',
    maxHeight: $previewSize,
    width: $previewSize,
    '> *': {
      maxHeight: $previewSize,
      maxWidth: $previewSize,
      color: $processing ? theme.color['icon-primary-disabled'] : theme.color['icon-core-subtle']
    }
  };
});
const StyledChildren = styled.div.withConfig({
  displayName: "FileAttachment__StyledChildren"
})(["position:absolute;top:0;right:0;bottom:0;left:0;"]);
export const FileAttachment = _ref => {
  let {
      children,
      closeable,
      onCloseClick,
      processing,
      use = 'default',
      size = 'lg',
      fileName,
      fileInfo,
      fileType,
      renderPreview
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const shorthandSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : size;
  const textColor = processing ? 'text-core-disabled' : 'text-core-default';
  const fileInfoColor = 'text-core-subtle';
  const renderedPreview = typeof renderPreview === 'function' ? renderPreview() : renderPreview;
  if (use === 'processing') {
    devLogger.warn({
      message: 'FileAttachment: `use="processing"` is deprecated. Set `processing={true}` instead.',
      key: 'FileAttachment: processing',
      url: 'https://git.hubteam.com/HubSpot/UIComponents/issues/7991#issuecomment-1176756'
    });
  }
  const attachmentPreviewSize = useIconSize(shorthandSize === 'sm' ? '300' : '800');
  const theme = useTheme();
  const closeButtonStyleOverrides = Object.assign({
    flexShrink: 0,
    color: theme.color['icon-core-default'],
    right: '10px',
    top: '10px',
    verticalAlign: 'text-top'
  }, shorthandSize === 'sm' && {
    alignSelf: 'center',
    marginRight: theme.space['0'],
    position: 'static',
    marginLeft: theme.space['200'],
    '&::after': {
      left: 'auto',
      marginLeft: theme.space['600']
    }
  });
  return /*#__PURE__*/_jsx(FileAttachmentProvider, {
    closeButtonStyleOverrides: closeButtonStyleOverrides,
    children: /*#__PURE__*/_jsxs(StyledFlexWrapper, Object.assign({
      $cssStyles: cssStyles,
      $processing: processing,
      $size: shorthandSize,
      $use: use,
      $closeable: closeable
    }, domProps, {
      children: [children && /*#__PURE__*/_jsx(StyledChildren, {
        children: children
      }), /*#__PURE__*/_jsxs(Media, {
        children: [/*#__PURE__*/_jsx(StyledAttachmentPreview, {
          $processing: processing,
          $previewSize: attachmentPreviewSize,
          children: renderedPreview || /*#__PURE__*/_jsx(DefaultFilePreview, {
            fileType: fileType,
            size: shorthandSize
          })
        }), /*#__PURE__*/_jsxs(Flex, Object.assign({
          order: 2,
          flex: 1,
          position: "relative",
          flexBasis: "auto",
          justifyContent: "center",
          flexDirection: "column",
          inlineSize: "100%",
          minInlineSize: "0"
        }, shorthandSize === 'sm' && {
          alignItems: 'center',
          whiteSpace: 'nowrap',
          columnGap: '200',
          flexDirection: 'row'
        }, {
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-25",
            color: textColor,
            children: /*#__PURE__*/_jsx(TruncateString, {
              children: fileName
            })
          }), fileInfo && /*#__PURE__*/_jsx(Flex, {
            flexShrink: 0,
            children: /*#__PURE__*/_jsx(Text, {
              as: "small",
              color: fileInfoColor,
              children: shorthandSize === 'sm' ? /*#__PURE__*/_jsxs(_Fragment, {
                children: ["(", fileInfo, ")"]
              }) : fileInfo
            })
          })]
        }))]
      }), closeable && /*#__PURE__*/_jsx(CloseButton, {
        onClick: onCloseClick,
        size: "sm",
        color: "icon-core-subtle"
      })]
    }))
  });
};
FileAttachment.displayName = 'FileAttachment';